import { useState, useEffect, useRef } from 'react';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { isAdmin } from '@/utils/roles';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { KITCHEN_URGENCY, kitchenLevel } from '@/theme/statusColors';
import { useKitchenThresholds } from '@/components/kitchen/useKitchenThresholds';
import { 
  Clock,
  ChefHat,
  Flame,
  CheckCircle2,
  Volume2,
  VolumeX,
  Maximize,
  RefreshCw,
  ArrowLeft,
  Home
} from 'lucide-react';
import axios from 'axios';
import ModernKitchen from '@/modern/staff/ModernKitchen';
import { useIsModern } from '@/modern/shared';
import { useNavigate } from 'react-router-dom';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function KitchenTV() {
  const { user } = useAuth();
  const { playSound, toggleSounds } = useNotificationSound();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [business, setBusiness] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const previousOrdersRef = useRef([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useLiveRefresh(() => fetchData(), 5000);
  useEffect(() => {
    fetchData();
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(timeInterval);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, businessRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/business`)
      ]);
      
      const kitchenOrders = ordersRes.data.filter(
        order => order.status === 'Confirmado' || order.status === 'En preparación'
      );
      
      // Check for new orders
      const previousIds = new Set(previousOrdersRef.current.map(o => o.order_id));
      const newOrders = kitchenOrders.filter(o => !previousIds.has(o.order_id) && o.status === 'Confirmado');
      
      if (newOrders.length > 0 && previousOrdersRef.current.length > 0) {
        if (soundEnabled) {
          playSound('newOrder');
        }
        setNewOrderIds(new Set(newOrders.map(o => o.order_id)));
        setTimeout(() => setNewOrderIds(new Set()), 3000);
      }
      
      previousOrdersRef.current = kitchenOrders;
      setOrders(kitchenOrders);
      setBusiness(businessRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getElapsedTime = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const kitchenT = useKitchenThresholds();
  const getUrgencyClass = (createdAt) => KITCHEN_URGENCY[kitchenLevel(createdAt, new Date(), kitchenT)].tv;

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'Confirmado');
  const preparingOrders = orders.filter(o => o.status === 'En preparación');

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
            <ChefHat className="w-10 h-10 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">COCINA</h1>
            <p className="text-muted-foreground">{business?.name}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Stats */}
          <div className="flex gap-4">
            <div className="text-center px-6 py-3 rounded-xl bg-blue-500/20 border border-blue-500/30">
              <p className="text-4xl font-bold text-blue-700">{pendingOrders.length}</p>
              <p className="text-sm text-blue-800">Pendientes</p>
            </div>
            <div className="text-center px-6 py-3 rounded-xl bg-orange-500/20 border border-orange-500/30">
              <p className="text-4xl font-bold text-orange-700">{preparingOrders.length}</p>
              <p className="text-sm text-orange-800">Preparando</p>
            </div>
          </div>
          
          {/* Time */}
          <div className="text-right px-6">
            <p className="text-4xl font-mono font-bold">
              {currentTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-sm text-muted-foreground">
              {currentTime.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'short' })}
            </p>
          </div>
          
          {/* Controls */}
          <div className="flex gap-2">
            {isAdmin(user) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              title="Volver al menú"
            >
              <Home className="w-6 h-6" />
            </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSoundEnabled(!soundEnabled); toggleSounds(!soundEnabled); }}
            >
              {soundEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleFullscreen}>
              <Maximize className="w-6 h-6" />
            </Button>
            <Button variant="ghost" size="icon" onClick={fetchData}>
              <RefreshCw className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Pending Column */}
        <div>
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            PENDIENTES ({pendingOrders.length})
          </h2>
          <div className="space-y-4">
            <AnimatePresence>
              {pendingOrders.map((order) => {
                const isNew = newOrderIds.has(order.order_id);
                return (
                  <motion.div
                    key={order.order_id}
                    initial={{ scale: 0.8, opacity: 0, x: -50 }}
                    animate={{ scale: 1, opacity: 1, x: 0 }}
                    exit={{ scale: 0.8, opacity: 0, x: 50 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card data-urgency={kitchenLevel(order.created_at, new Date(), kitchenT)} className={`${getUrgencyClass(order.created_at)} ${isNew ? 'ring-4 ring-blue-500' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Badge className="bg-blue-700 text-lg px-3 py-1">{order.unit_name || 'Mesa'}</Badge>
                            <span className="text-sm text-muted-foreground">#{order.order_id?.slice(-6)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="w-5 h-5" />
                            <span className="text-lg font-mono">{getElapsedTime(order.created_at)}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-4 bg-muted/50 p-3 rounded-lg max-h-40 overflow-y-auto">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-lg">
                              <span>
                                <span className="font-bold text-foreground">{item.quantity}x</span>{' '}
                                {item.product_name}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        {order.notes && (
                          <p className="text-sm text-amber-800 mb-3 p-2 bg-amber-500/10 rounded">
                            Nota: {order.notes}
                          </p>
                        )}
                        
                        <Button 
                          className="w-full h-12 text-lg bg-orange-700 hover:bg-orange-800"
                          onClick={() => updateOrderStatus(order.order_id, 'En preparación')}
                        >
                          <Flame className="w-5 h-5 mr-2" />
                          Preparar
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            
            {pendingOrders.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-xl">Sin pedidos pendientes</p>
              </div>
            )}
          </div>
        </div>

        {/* Preparing Column */}
        <div>
          <h2 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2">
            <Flame className="w-6 h-6" />
            EN PREPARACIÓN ({preparingOrders.length})
          </h2>
          <div className="space-y-4">
            <AnimatePresence>
              {preparingOrders.map((order) => (
                <motion.div
                  key={order.order_id}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0, x: 50 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card data-urgency={kitchenLevel(order.created_at, new Date(), kitchenT)} className={`border-orange-500 bg-orange-950/30 ${getUrgencyClass(order.created_at)}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-orange-700 text-lg px-3 py-1">{order.unit_name || 'Mesa'}</Badge>
                          <span className="text-sm text-muted-foreground">#{order.order_id?.slice(-6)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-orange-700">
                          <Clock className="w-5 h-5" />
                          <span className="text-lg font-mono">{getElapsedTime(order.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4 bg-muted/50 p-3 rounded-lg max-h-40 overflow-y-auto">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-lg">
                            <span>
                              <span className="font-bold text-foreground">{item.quantity}x</span>{' '}
                              {item.product_name}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {order.notes && (
                        <p className="text-sm text-amber-800 mb-3 p-2 bg-amber-500/10 rounded">
                          Nota: {order.notes}
                        </p>
                      )}
                      
                      <Button 
                        className="w-full h-12 text-lg bg-green-700 hover:bg-green-800"
                        onClick={() => updateOrderStatus(order.order_id, 'Listo')}
                      >
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                        Listo para Entregar
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
            
            {preparingOrders.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Flame className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-xl">Nada en preparación</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function KitchenTVPage() {
  return useIsModern() ? <ModernKitchen tv /> : <KitchenTV />;
}
